import type { PaginatedResponse } from '@kizen/api';
import type {
  GetTeamMembersParams,
  GetTeamMembersResponse,
  SearchTeamMembersOptions,
  TeamMember,
} from '@kizen/api/team-member';
import type { DefaultError, QueryObserverOptions } from '@tanstack/query-core';
import type { AxiosInstance } from 'axios';
import type { Resource } from './resource';
import type { Drop, WithParams, WithRequired } from './types';

import { getTeamMembers, searchTeamMembers } from '@kizen/api/team-member';
import { getResource } from './resource';
import { curry1 } from './utils';

type TeamMemberQueryOptions<
  TParams extends GetTeamMembersParams | undefined,
  TData = GetTeamMembersResponse,
> = WithParams<
  Omit<TeamMembersQuery<TParams, TData>, 'queryFn' | 'queryKey'>,
  TParams
>;

type TeamMembersQuery<
  TParams extends GetTeamMembersParams | undefined,
  TData = GetTeamMembersResponse<TParams>,
> = WithRequired<
  QueryObserverOptions<
    GetTeamMembersResponse<TParams>,
    DefaultError,
    TData,
    GetTeamMembersResponse<TParams>,
    [Resource<Readonly<['team']>, TParams>]
  >,
  'queryFn' | 'queryKey'
>;

interface SearchTeamMemberQueryOptions<TData = PaginatedResponse<TeamMember>>
  extends WithParams<
    Omit<SearchTeamMembersQuery<TData>, 'queryFn' | 'queryKey'>,
    SearchTeamMembersOptions['params']
  > {
  body?: SearchTeamMembersOptions['body'];
}

type SearchTeamMembersQuery<TData = PaginatedResponse<TeamMember>> =
  WithRequired<
    QueryObserverOptions<
      PaginatedResponse<TeamMember>,
      DefaultError,
      TData,
      PaginatedResponse<TeamMember>,
      [
        Resource<
          Readonly<['team', 'search']>,
          SearchTeamMembersOptions['params'],
          SearchTeamMembersOptions['body']
        >,
      ]
    >,
    'queryFn' | 'queryKey'
  >;

export const teamMembersQuery = <
  TParams extends GetTeamMembersParams | undefined,
  TData = GetTeamMembersResponse,
>(
  axios: AxiosInstance,
  options: TeamMemberQueryOptions<TParams, TData> = {}
): TeamMembersQuery<TParams, TData> => {
  const path = ['team'] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getTeamMembers(axios, {
        params: qk.params,
        signal,
      });
    },
    ...queryOpts,
  };
};

export const searchTeamMembersQuery = <TData = PaginatedResponse<TeamMember>>(
  axios: AxiosInstance,
  options: SearchTeamMemberQueryOptions<TData> = {}
): SearchTeamMembersQuery<TData> => {
  const path = ['team', 'search'] as const;
  const { body, params, ...queryOpts } = options;
  const resource = getResource(path, { body, params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return searchTeamMembers(axios, {
        body: qk.body,
        params: qk.params,
        signal,
      });
    },
    ...queryOpts,
  };
};

class Queries {
  public teamMembersQuery: <
    TParams extends GetTeamMembersParams | undefined = undefined,
    TData = GetTeamMembersResponse,
  >(
    ...args: Drop<Parameters<typeof teamMembersQuery<TParams, TData>>>
  ) => ReturnType<typeof teamMembersQuery<TParams, TData>>;

  public searchTeamMembersQuery: <TData = GetTeamMembersResponse>(
    ...args: Drop<Parameters<typeof searchTeamMembersQuery<TData>>>
  ) => ReturnType<typeof searchTeamMembersQuery<TData>>;

  constructor(axios: AxiosInstance) {
    this.teamMembersQuery = curry1(teamMembersQuery)(axios);
    this.searchTeamMembersQuery = curry1(searchTeamMembersQuery)(axios);
  }
}

export type TeamMemberQueries = Queries;

export const defTeamMemberQueries = (
  axios: AxiosInstance
): TeamMemberQueries => {
  return new Queries(axios);
};
